import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Chip, Paper, Stack, TextField, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PageLayout from '../shell/PageLayout';
import { useOperations } from './OperationsProvider';
import OperationsMap, { located } from './OperationsMap';
import { humanMs } from './areas';
import { formatDateTime } from '../common/util/datetime';
import usePersistedState from '../common/util/usePersistedState';
import VehicleCard from './VehicleCard';
import { reportingState, NOT_ON_TRIP } from './reporting';

// Re-exported so existing importers keep working now the logic lives on its own.
export { reportingState, NOT_ON_TRIP };

// Vehicle-centric tracking, for the person whose job is the vehicles rather than the plant. A user
// given access to a handful of trucks sees those trucks, where they are and when each last
// reported — the server decides which vehicles that is, so scope is not a matter of what this page
// chooses to draw. The tracking application this was forked from had a screen like this, but it
// spoke of devices rather than trucks, drew no plant or parking boundaries, and read a data layer
// this product no longer fills. This one is built from the same bootstrap and the same map as the
// rest of the product, so an area drawn in Configuration appears here too.

// The kind of thing it is, at a glance, the way a fleet list has always shown it.
const icons = { Tanker: LocalGasStationIcon, Van: AirportShuttleIcon, Other: DirectionsCarIcon };
const Silhouette = ({ type }) => {
  const Icon = icons[type] || LocalShippingIcon;
  return <Icon fontSize="small" sx={{ color: 'text.disabled' }} />;
};

// "14 min ago" is read faster than a timestamp, and is the thing being judged: how stale this is.
const ago = (value) => {
  const at = Date.parse(value);
  if (!Number.isFinite(at)) return null;
  const since = Date.now() - at;
  return since < 60000 ? 'just now' : `${humanMs(since)} ago`;
};

const LiveTrackingPage = () => {
  const { data, trips, error: loadError } = useOperations();
  const positions = useSelector((s) => s.positions.items);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  // Watching a yard is a whole-screen job, so the list folds away and stays folded: someone who
  // works from the map should not have to close it again every time they open the page. Hidden, it
  // still says how many vehicles it is holding, and a marker on the map remains selectable.
  const [listOpen, setListOpen] = usePersistedState('liveVehicleList', true);
  const vehicles = data.vehicles || [];
  const intervalMs = data.config?.reportingIntervalMs || 0;
  const rows = useMemo(
    () => vehicles
      .map((v) => ({ vehicle: v, position: positions[v.id], state: reportingState(positions[v.id], intervalMs) }))
      .filter(({ vehicle }) => vehicle.name.toLowerCase().includes(search.trim().toLowerCase()))
      .sort((a, b) => Number(a.state.quiet) - Number(b.state.quiet) || a.vehicle.name.localeCompare(b.vehicle.name)),
    [vehicles, positions, intervalMs, search],
  );
  const chosen = rows.find(({ vehicle }) => vehicle.id === selected);
  // Most of a fleet is parked at any moment, so the card falls back to the last finished trip
  // rather than saying nothing about fifteen trucks out of eighteen.
  const tripFor = (id) => {
    const mine = (trips || []).filter((t) => t.vehicleId === id);
    return mine.find((t) => !t.closedAt)
      || mine.filter((t) => t.closedAt).sort((a, b) => Date.parse(b.closedAt) - Date.parse(a.closedAt))[0]
      || null;
  };
  const points = rows.map(({ vehicle, position, state }) => ({
    ...position,
    id: vehicle.id,
    label: vehicle.name,
    color: state.quiet ? '#64748b' : '#008c75',
  })).filter(located);
  return (
    <PageLayout title="Live Tracking" subtitle="Where your vehicles are now, and when each last reported.">
      {loadError && <Alert severity="warning" sx={{ mb: 2 }}>{loadError}</Alert>}
      {!vehicles.length && <Alert severity="info" sx={{ mb: 2 }}>No vehicles are visible to you. A tenant administrator grants access to the vehicles you should track.</Alert>}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
        {/* Folded, the column goes entirely rather than standing empty beside the map: a button is
            not worth a gutter. Its own control moves above the map, where the list will reappear. */}
        {listOpen && (
        <Stack spacing={2} sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0 }}>
          <Button size="small" variant="outlined" startIcon={<ChevronLeftIcon />} onClick={() => setListOpen(false)} sx={{ alignSelf: 'flex-start' }}>Hide list</Button>
          <TextField fullWidth size="small" label="Find a vehicle" value={search} onChange={(e) => setSearch(e.target.value)} inputProps={{ maxLength: 60 }} />
          <Paper variant="outlined" sx={{ maxHeight: 520, overflowY: 'auto' }}>
            {!rows.length && <Typography sx={{ p: 2 }} color="text.secondary">No vehicle matches that name.</Typography>}
            {rows.map(({ vehicle, position, state }) => (
              <Box
                key={vehicle.id}
                role="button"
                tabIndex={0}
                aria-label={`Show ${vehicle.name} on the map`}
                onClick={() => setSelected(vehicle.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') setSelected(vehicle.id); }}
                sx={{ p: 1.5, cursor: 'pointer', borderBottom: 1, borderColor: 'divider', bgcolor: selected === vehicle.id ? 'action.selected' : 'transparent' }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Silhouette type={vehicle.vehicleType} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{vehicle.name}</Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      color={state.quiet ? 'error.main' : 'text.secondary'}
                      title={position?.fixTime ? formatDateTime(position.fixTime) : undefined}
                      noWrap
                    >
                      {state.withheld
                        ? 'Shown while it runs your trip or stands at your site'
                        : (position?.fixTime ? ago(position.fixTime) : 'Never reported a position')}
                    </Typography>
                  </Box>
                  <Chip size="small" label={state.label} color={state.color} variant={state.color === 'default' ? 'outlined' : 'filled'} />
                </Stack>
                {!located(position) && !!position && <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 4 }}>Last fix has no usable coordinates.</Typography>}
              </Box>
            ))}
          </Paper>
        </Stack>
        )}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {!listOpen && <Button size="small" variant="outlined" startIcon={<ChevronRightIcon />} onClick={() => setListOpen(true)} sx={{ mb: 2 }}>{`Show list (${rows.length})`}</Button>}
          <OperationsMap
            height={520}
            areas={data.areas}
            points={points}
            selectedId={selected}
            onSelect={setSelected}
            card={chosen ? (
              <VehicleCard
                vehicle={chosen.vehicle}
                position={chosen.position}
                state={chosen.state}
                trip={tripFor(chosen.vehicle.id)}
              />
            ) : null}
          />
        </Box>
      </Stack>
    </PageLayout>
  );
};

export default LiveTrackingPage;
